import { useRef, useState } from "react";
import { isPdfFile, formatFileSize } from "../../utils/pdfFiles";
import "./PdfUpload.css";

/** Drag-and-drop / click-to-browse zone. Hands back only the PDFs; anything else is
 *  reported as a hint so the user knows why a file didn't show up. */
export function PdfDropZone({ onFiles, compact = false }) {
    const inputRef = useRef(null);
    const [dragging, setDragging] = useState(false);
    const [rejected, setRejected] = useState(0);
    const dragDepth = useRef(0); // dragenter/leave fire for every child element

    const accept = (fileList) => {
        const all = Array.from(fileList || []);
        const pdfs = all.filter(isPdfFile);
        setRejected(all.length - pdfs.length);
        if (pdfs.length) onFiles(pdfs);
    };

    return (
        <div
            className={`pdfu-drop ${compact ? "pdfu-drop--compact" : ""} ${dragging ? "is-dragging" : ""}`}
            role="button"
            tabIndex={0}
            onClick={() => inputRef.current?.click()}
            onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); inputRef.current?.click(); } }}
            onDragEnter={e => { e.preventDefault(); dragDepth.current += 1; setDragging(true); }}
            onDragOver={e => e.preventDefault()}
            onDragLeave={() => { dragDepth.current -= 1; if (dragDepth.current <= 0) setDragging(false); }}
            onDrop={e => {
                e.preventDefault();
                dragDepth.current = 0;
                setDragging(false);
                accept(e.dataTransfer.files);
            }}
        >
            <input
                ref={inputRef}
                type="file"
                accept="application/pdf,.pdf"
                multiple
                hidden
                onChange={e => { accept(e.target.files); e.target.value = ""; }}
            />
            <div className="pdfu-drop-icon">
                <i className={`bi ${dragging ? "bi-file-earmark-arrow-down" : "bi-cloud-arrow-up"}`} />
            </div>
            <div className="pdfu-drop-title">
                {dragging ? "Drop to add" : <>Drag & drop PDFs here or <span className="pdfu-link">browse</span></>}
            </div>
            <div className="pdfu-drop-hint">You can pick several files at once</div>
            {rejected > 0 && (
                <div className="pdfu-drop-warn">
                    {rejected} file{rejected > 1 ? "s were" : " was"} skipped — only PDFs are supported
                </div>
            )}
        </div>
    );
}

/** Chosen-but-not-yet-opened files, with per-file remove. */
export function PdfFileList({ files, onRemove }) {
    if (!files.length) return null;
    return (
        <ul className="pdfu-list">
            {files.map((file, i) => (
                <li key={`${file.name}-${file.size}-${file.lastModified}`} className="pdfu-list-item">
                    <i className="bi bi-file-earmark-pdf-fill pdfu-list-icon" />
                    <span className="pdfu-list-name" title={file.name}>{file.name}</span>
                    <span className="pdfu-list-size">{formatFileSize(file.size)}</span>
                    <button className="pdfu-icon-btn" title="Remove" onClick={() => onRemove(i)}>
                        <i className="bi bi-x-lg" />
                    </button>
                </li>
            ))}
        </ul>
    );
}

/** Adds files to a list, dropping exact duplicates of files already in it. */
export const mergeFiles = (current, incoming) => {
    const key = f => `${f.name}|${f.size}|${f.lastModified}`;
    const seen = new Set(current.map(key));
    return [...current, ...incoming.filter(f => !seen.has(key(f)))];
};

/** Single-line "open from link" form. `onLoad(url)` should throw to surface an error. */
export function PdfUrlForm({ onLoad }) {
    const [url, setUrl] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        const value = url.trim();
        if (!value) return;
        if (!/^https?:\/\//i.test(value)) { setError("Enter a full link starting with http:// or https://"); return; }
        setBusy(true);
        setError("");
        try {
            await onLoad(value);
            setUrl("");
        } catch (err) {
            setError(err.message || "Couldn't load that PDF");
        } finally {
            setBusy(false);
        }
    };

    return (
        <form className="pdfu-url" onSubmit={submit}>
            <div className="pdfu-url-row">
                <i className="bi bi-link-45deg pdfu-url-icon" />
                <input
                    type="text"
                    value={url}
                    onChange={e => { setUrl(e.target.value); setError(""); }}
                    placeholder="https://example.com/document.pdf"
                    disabled={busy}
                />
                <button type="submit" className="pdfu-btn pdfu-btn--secondary" disabled={busy || !url.trim()}>
                    {busy ? <span className="pdfu-spinner" /> : "Open"}
                </button>
            </div>
            {error && <div className="pdfu-error">{error}</div>}
        </form>
    );
}
